import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { AxiosError } from "axios";
import {
  CheckCircle2,
  KeyRound,
  Loader2,
  LockKeyhole,
  LogIn,
  LogOut,
  Mail,
  ShieldCheck,
  Signature,
  UserCheck,
} from "lucide-react";
import { useAuthStore } from "@shared/store/auth";
import {
  getPublicNcpSignatureRequest,
  sendPublicNcpSignatureCode,
  signPublicNcpDocument,
  type PublicNcpSignatureRequest,
} from "../api/ncpSignature";

const TOKEN_PATTERN = /^[a-f0-9]{64}$/;

export function NcpSignaturePage({ token }: { token: string }) {
  const me = useAuthStore((state) => state.me);
  const ensureLoaded = useAuthStore((state) => state.ensureLoaded);
  const login = useAuthStore((state) => state.login);
  const logout = useAuthStore((state) => state.logout);

  const [requestData, setRequestData] = useState<PublicNcpSignatureRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [authChecking, setAuthChecking] = useState(true);
  const [authenticating, setAuthenticating] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [signing, setSigning] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [codigo, setCodigo] = useState("");
  const [codeRequested, setCodeRequested] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [signedBy, setSignedBy] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    async function load() {
      if (!TOKEN_PATTERN.test(token)) {
        setError("El enlace de firma no es válido.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const data = await getPublicNcpSignatureRequest(token);
        if (active) setRequestData(data);
      } catch (requestError) {
        if (active) setError(readApiError(requestError, "No se pudo abrir la solicitud de firma."));
      } finally {
        if (active) setLoading(false);
      }
    }

    void load();
    return () => {
      active = false;
    };
  }, [token]);

  useEffect(() => {
    let active = true;

    void ensureLoaded().finally(() => {
      if (active) setAuthChecking(false);
    });

    return () => {
      active = false;
    };
  }, [ensureLoaded]);

  const canOperate = requestData?.status === "pendiente";
  const expiryText = useMemo(() => formatDateTime(requestData?.expires_at ?? null), [requestData?.expires_at]);
  const requiredPermission = requestData ? permissionForSignature(requestData.tipo) : null;
  const canCurrentUserSign = Boolean(
    me
    && requiredPermission
    && me.permissions.includes(requiredPermission),
  );

  const authenticate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!email.trim() || !password) {
      setError("Introduce el correo y la contraseña de tu cuenta de INPRONEXUS.");
      return;
    }

    setAuthenticating(true);
    setError(null);
    setInfo(null);
    try {
      await login(email.trim(), password, false);
      setPassword("");
      setInfo("Identidad verificada. Ya puedes solicitar el código de firma.");
    } catch (authError) {
      setError(readApiError(authError, "No se pudo iniciar sesión. Revisa tus credenciales."));
    } finally {
      setAuthenticating(false);
    }
  };

  const switchAccount = async () => {
    setError(null);
    setInfo(null);
    setCodeRequested(false);
    setCodigo("");
    await logout();
  };

  const requestCode = async () => {
    if (!me) {
      setError("Debes iniciar sesión en INPRONEXUS antes de solicitar el código.");
      return;
    }
    if (!canCurrentUserSign) {
      setError("Tu usuario no dispone del permiso necesario para esta firma.");
      return;
    }

    setSendingCode(true);
    setError(null);
    setInfo(null);
    try {
      const message = await sendPublicNcpSignatureCode(token);
      setInfo(message);
      setCodeRequested(true);
    } catch (requestError) {
      setError(readApiError(requestError, "No se pudo solicitar el código."));
    } finally {
      setSendingCode(false);
    }
  };

  const sign = async () => {
    if (!me) {
      setError("La sesión ha finalizado. Inicia sesión de nuevo antes de firmar.");
      return;
    }
    if (!/^[0-9]{6}$/.test(codigo)) {
      setError("Introduce el código de 6 dígitos recibido por correo.");
      return;
    }

    setSigning(true);
    setError(null);
    try {
      const firma = await signPublicNcpDocument(token, codigo);
      setSignedBy(firma.usuario_nombre);
      setRequestData((current) => current ? {
        ...current,
        status: "firmada",
        firmada_en: firma.firmado_en,
      } : current);
    } catch (requestError) {
      setError(readApiError(requestError, "No se pudo completar la firma."));
    } finally {
      setSigning(false);
    }
  };

  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,.18),transparent_34%),linear-gradient(180deg,#f7fbf9_0%,#edf6f2_100%)] px-4 py-8 text-slate-900 sm:px-6">
      <div className="mx-auto max-w-xl">
        <header className="mb-5 flex items-center justify-between rounded-[28px] border border-emerald-100 bg-white/90 px-5 py-4 shadow-[0_20px_60px_rgba(15,23,42,.08)] backdrop-blur">
          <div>
            <div className="text-xs font-black uppercase tracking-[0.2em] text-emerald-700">INPRONEXUS</div>
            <div className="mt-1 text-sm font-bold text-slate-500">Firma segura de Informe NCp</div>
          </div>
          <div className="grid h-12 w-12 place-items-center rounded-2xl border border-emerald-100 bg-emerald-50 text-emerald-700">
            <ShieldCheck size={24} />
          </div>
        </header>

        <section className="overflow-hidden rounded-[32px] border border-slate-200/80 bg-white shadow-[0_28px_80px_rgba(15,23,42,.12)]">
          <div className="border-b border-slate-100 px-6 py-6">
            <div className="flex items-center gap-3">
              <div className="grid h-11 w-11 place-items-center rounded-2xl bg-slate-900 text-white">
                <Signature size={21} />
              </div>
              <div>
                <h1 className="text-xl font-black tracking-tight">Validar firma</h1>
                <p className="mt-1 text-sm font-semibold text-slate-500">La firma quedará vinculada a tu cuenta de INPRONEXUS y a esta versión del documento.</p>
              </div>
            </div>
          </div>

          <div className="space-y-5 p-6">
            {loading ? (
              <StateCard icon={<Loader2 className="animate-spin" size={22} />} title="Comprobando solicitud" copy="Estamos verificando el enlace de firma." />
            ) : error && !requestData ? (
              <StateCard icon={<ShieldCheck size={22} />} title="No se puede continuar" copy={error} danger />
            ) : requestData ? (
              <>
                <div className="grid gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4 text-sm sm:grid-cols-2">
                  <Info label="Informe" value={`NCp Nº ${requestData.numero ?? "-"}`} />
                  <Info label="Firma" value={requestData.tipo_label} />
                  <Info label="Versión" value={String(requestData.documento_version)} />
                  <Info label="Caduca" value={expiryText} />
                </div>

                {requestData.status === "firmada" ? (
                  <StateCard
                    icon={<CheckCircle2 size={24} />}
                    title="Firma completada"
                    copy={signedBy ? `La firma se ha registrado correctamente a nombre de ${signedBy}.` : "Esta solicitud ya ha sido firmada correctamente."}
                  />
                ) : requestData.status === "caducada" ? (
                  <StateCard icon={<ShieldCheck size={22} />} title="Código QR caducado" copy="Solicita un nuevo código QR desde el Informe NCp." danger />
                ) : requestData.status === "invalidada" ? (
                  <StateCard icon={<ShieldCheck size={22} />} title="Solicitud invalidada" copy="El informe ha cambiado o se ha generado una solicitud posterior. Solicita un nuevo QR." danger />
                ) : authChecking ? (
                  <StateCard icon={<Loader2 className="animate-spin" size={22} />} title="Comprobando sesión" copy="Estamos verificando si ya tienes una sesión activa de INPRONEXUS en este dispositivo." />
                ) : !me ? (
                  <form onSubmit={(event) => void authenticate(event)} className="space-y-4">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-start gap-3">
                        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white text-slate-700 shadow-sm">
                          <LogIn size={19} />
                        </span>
                        <div>
                          <div className="font-black text-slate-800">Identifícate con tu cuenta</div>
                          <p className="mt-1 text-sm font-semibold leading-6 text-slate-500">El firmante será siempre el usuario que inicie sesión. No se puede indicar manualmente el nombre ni el correo de otra persona.</p>
                        </div>
                      </div>
                    </div>

                    <label className="block">
                      <span className="mb-2 flex items-center gap-2 text-xs font-black uppercase tracking-[0.14em] text-slate-500">
                        <Mail size={14} /> Correo de INPRONEXUS
                      </span>
                      <input
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        disabled={authenticating}
                        placeholder="usuario@empresa.com"
                        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 disabled:bg-slate-100"
                      />
                    </label>

                    <label className="block">
                      <span className="mb-2 flex items-center gap-2 text-xs font-black uppercase tracking-[0.14em] text-slate-500">
                        <LockKeyhole size={14} /> Contraseña
                      </span>
                      <input
                        type="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        disabled={authenticating}
                        placeholder="Contraseña de INPRONEXUS"
                        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 disabled:bg-slate-100"
                      />
                    </label>

                    <button
                      type="submit"
                      disabled={authenticating}
                      className="flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-black text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {authenticating ? <Loader2 size={17} className="animate-spin" /> : <LogIn size={17} />}
                      {authenticating ? "Verificando credenciales..." : "Iniciar sesión y continuar"}
                    </button>

                    {error ? <div role="alert" className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">{error}</div> : null}
                  </form>
                ) : (
                  <>
                    <div className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4">
                      <div className="flex items-start gap-3">
                        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white text-emerald-700 shadow-sm">
                          <UserCheck size={19} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] font-black uppercase tracking-[0.14em] text-emerald-700">Usuario identificado</div>
                          <div className="mt-1 font-black text-slate-900">{me.user.name}</div>
                          <div className="mt-0.5 break-all text-sm font-semibold text-slate-500">{me.user.email}</div>
                        </div>
                      </div>
                    </div>

                    {!canCurrentUserSign ? (
                      <>
                        <StateCard
                          icon={<ShieldCheck size={22} />}
                          title="Usuario sin permiso de firma"
                          copy={`La cuenta ${me.user.email} no puede firmar como ${requestData.tipo_label}. Utiliza una cuenta autorizada.`}
                          danger
                        />
                        <button
                          type="button"
                          onClick={() => void switchAccount()}
                          className="flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700 transition hover:bg-slate-50"
                        >
                          <LogOut size={17} />
                          Cerrar sesión y usar otra cuenta
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold leading-6 text-slate-600">
                          El código de verificación se enviará al correo registrado de esta cuenta: <strong className="text-slate-900">{maskEmail(me.user.email)}</strong>.
                        </div>

                        <button
                          type="button"
                          onClick={() => void requestCode()}
                          disabled={!canOperate || sendingCode || signing}
                          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-black text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {sendingCode ? <Loader2 size={17} className="animate-spin" /> : <Mail size={17} />}
                          {sendingCode ? "Enviando código..." : codeRequested ? "Enviar un nuevo código" : "Enviar código de verificación"}
                        </button>

                        {codeRequested ? (
                          <>
                            <label className="block">
                              <span className="mb-2 flex items-center gap-2 text-xs font-black uppercase tracking-[0.14em] text-slate-500">
                                <KeyRound size={14} /> Código recibido
                              </span>
                              <input
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                maxLength={6}
                                value={codigo}
                                onChange={(event) => setCodigo(event.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                                disabled={signing}
                                placeholder="000000"
                                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-center text-2xl font-black tracking-[0.35em] outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 disabled:bg-slate-100"
                              />
                            </label>

                            <button
                              type="button"
                              onClick={() => void sign()}
                              disabled={signing || codigo.length !== 6}
                              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-700 px-4 py-3.5 text-sm font-black text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {signing ? <Loader2 size={18} className="animate-spin" /> : <Signature size={18} />}
                              {signing ? "Registrando firma..." : "Verificar y firmar documento"}
                            </button>
                          </>
                        ) : null}

                        <button
                          type="button"
                          onClick={() => void switchAccount()}
                          disabled={sendingCode || signing}
                          className="flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-black text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <LogOut size={15} />
                          Usar otra cuenta
                        </button>

                        {info ? <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-800">{info}</div> : null}
                        {error ? <div role="alert" className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">{error}</div> : null}
                      </>
                    )}
                  </>
                )}
              </>
            ) : null}
          </div>
        </section>

        <p className="mt-4 text-center text-xs font-semibold leading-5 text-slate-400">
          La firma requiere cuenta INPRONEXUS, permiso específico y código temporal enviado al correo registrado. Se conservan usuario, fecha, versión y huella del documento.
        </p>
      </div>
    </main>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] font-black uppercase tracking-[0.14em] text-emerald-700">{label}</div>
      <div className="mt-1 font-black text-slate-800">{value}</div>
    </div>
  );
}

function StateCard({
  icon,
  title,
  copy,
  danger = false,
}: {
  icon: ReactNode;
  title: string;
  copy: string;
  danger?: boolean;
}) {
  return (
    <div className={`rounded-2xl border p-5 ${danger ? "border-rose-200 bg-rose-50 text-rose-800" : "border-emerald-100 bg-emerald-50 text-emerald-800"}`}>
      <div className="flex items-start gap-3">
        <span className="mt-0.5">{icon}</span>
        <div>
          <div className="font-black">{title}</div>
          <p className="mt-1 text-sm font-semibold leading-6 opacity-80">{copy}</p>
        </div>
      </div>
    </div>
  );
}

function permissionForSignature(tipo: PublicNcpSignatureRequest["tipo"]): string {
  return tipo === "direccion"
    ? "sign-quality-ncp-direccion"
    : "sign-quality-ncp-responsable";
}

function maskEmail(email: string): string {
  const [local, domain] = email.split("@");
  if (!local || !domain) return email;
  const visible = local.slice(0, Math.min(2, local.length));
  return `${visible}${"*".repeat(Math.max(3, local.length - visible.length))}@${domain}`;
}

function formatDateTime(value: string | null): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function readApiError(error: unknown, fallback: string): string {
  if (error instanceof AxiosError) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    const firstValidationError = data?.errors ? Object.values(data.errors).flat()[0] : null;
    return firstValidationError ?? data?.message ?? fallback;
  }
  return fallback;
}
